import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, vShow as _vShow, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "recommend-edit__header" }
const _hoisted_2 = { class: "recommend-edit__title" }
const _hoisted_3 = ["aria-label"]

import Mdi from '@/corePagesModule/components/Mdi.vue';
import { IRecommendEditPanelResizeState } from '@/recommendOrderModule/pages/interfaces/recommendOrder';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'RecommendEditWrapper',
  props: {
    isActive: { type: Boolean },
    title: {},
    expandedLessDesc: {},
    expandedMoreDesc: {},
    isGrowing: { type: Boolean },
    resizable: { type: Boolean },
    resizeState: {}
  },
  emits: ["update:resizeState"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emits = __emit;

const isExpanded = ref<boolean>(true);
const wrapperRef = ref<HTMLElement | null>(null);
const isResizing = ref<boolean>(false);
const resizedHeight = ref<number>();
const hasUserResized = ref(false);

let resizingPointerId: number | undefined;
let resizeHandle: HTMLElement | undefined;
let startY = 0;
let startHeight = 0;
let initialHeight: number | undefined;

const textIconState = computed((): [label: string | undefined, icon: string] =>
  isExpanded.value ? [props.expandedLessDesc, 'expand_less'] : [props.expandedMoreDesc, 'expand_more']
);

/**
 * リサイズ状態を親へ保存する
 */
const saveResizeState = () => {
  emits('update:resizeState', {
    height: resizedHeight.value,
    minHeight: initialHeight,
    hasUserResized: hasUserResized.value,
  });
};

const wrapperStyle = computed(() => {
  if ((!isResizing.value && !hasUserResized.value) || resizedHeight.value == null || !isExpanded.value) {
    return;
  }

  return {
    height: `${resizedHeight.value}px`,
    flex: '0 0 auto',
  };
});

/**
 * リサイズ開始
 * @param event - イベント
 */
const startResize = (event: PointerEvent) => {
  if (!wrapperRef.value || event.button !== 0) return;

  event.preventDefault();

  const currentHeight = wrapperRef.value.getBoundingClientRect().height;

  // flex-growで表示されている場合は、現在の割当高をリサイズ下限へ反映する。
  // 保存済みの高さを先に適用するとpointerdown時にパネルが縮むため、固定高も現在値へ同期する。
  if (!hasUserResized.value) {
    initialHeight = currentHeight;
    resizedHeight.value = currentHeight;
  }

  resizeHandle = event.currentTarget as HTMLElement;
  resizingPointerId = event.pointerId;
  resizeHandle.setPointerCapture(event.pointerId);

  isResizing.value = true;
  document.documentElement.classList.add('is-resizing-vertical');

  // 初回の高さを最小サイズとして保存
  initialHeight ??= currentHeight;

  startY = event.clientY;
  startHeight = currentHeight;
};

/**
 * リサイズ状態を解除する
 */
const finishResize = () => {
  const handle = resizeHandle;
  const pointerId = resizingPointerId;

  isResizing.value = false;
  resizingPointerId = undefined;
  resizeHandle = undefined;
  document.documentElement.classList.remove('is-resizing-vertical');

  if (handle && pointerId !== undefined && handle.hasPointerCapture(pointerId)) {
    handle.releasePointerCapture(pointerId);
  }

  saveResizeState();
};

/**
 * リサイズイベント
 * @param event - イベント
 */
const resize = (event: PointerEvent) => {
  if (!isResizing.value || event.pointerId !== resizingPointerId) return;

  // ウィンドウ外でpointerupを取り逃した場合、再入場時にリサイズを終了する
  if (event.buttons === 0) {
    finishResize();
    return;
  }

  event.preventDefault();

  const calculatedHeight = startHeight + event.clientY - startY;
  const nextHeight = Math.max(initialHeight ?? 0, calculatedHeight);

  resizedHeight.value = nextHeight;
  hasUserResized.value = initialHeight != null && nextHeight > initialHeight;
};

/**
 * リサイズ停止
 * @param event - イベント
 */
const stopResize = (event?: PointerEvent) => {
  if (event && resizingPointerId !== undefined && event.pointerId !== resizingPointerId) {
    return;
  }

  finishResize();
};

/**
 * 開閉トグル
 */
const toggleExpanded = () => {
  if (props.isActive) return;
  isExpanded.value = !isExpanded.value;
};

/**
 * 初期高さリセット
 */
const resetInitialHeight = () => {
  if (hasUserResized.value) return;
  initialHeight = undefined;
  resizedHeight.value = undefined;
};

watch(
  () => props.isActive,
  (isActive) => {
    if (isActive) {
      isExpanded.value = true;
    }
  }
);

watch(
  () => props.resizeState,
  (resizeState) => {
    if (isResizing.value) return;
    resizedHeight.value = resizeState?.height;
    initialHeight = resizeState?.minHeight;
    hasUserResized.value = resizeState?.hasUserResized ?? false;
  },
  { immediate: true }
);

onMounted(() => {
  window.addEventListener('blur', finishResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('blur', finishResize);
  finishResize();
  resetInitialHeight();
});

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", {
    ref_key: "wrapperRef",
    ref: wrapperRef,
    class: _normalizeClass(["recommend-edit__wrapper", {
      'recommend-edit__wrapper--closed': !isExpanded.value,
      'recommend-edit__wrapper--active': _ctx.isActive,
      'recommend-edit__wrapper--grow': _ctx.isGrowing && isExpanded.value,
    }]),
    style: _normalizeStyle(wrapperStyle.value)
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("span", _hoisted_2, _toDisplayString(props.title), 1),
      (!props.isActive)
        ? _withDirectives((_openBlock(), _createElementBlock("button", {
            key: 0,
            "aria-label": textIconState.value[0],
            class: "recommend-edit__expand",
            onClick: toggleExpanded
          }, [
            _createVNode(Mdi, {
              icon: textIconState.value[1]
            }, null, 8, ["icon"])
          ], 8, _hoisted_3)), [
            [_directive_tooltip, textIconState.value[0]]
          ])
        : _createCommentVNode("", true)
    ]),
    _withDirectives(_createElementVNode("div", {
      class: _normalizeClass(["recommend-edit__content", { 'recommend-edit__content--inactive': !_ctx.isActive }])
    }, [
      _renderSlot(_ctx.$slots, "default")
    ], 2), [
      [_vShow, isExpanded.value]
    ]),
    (props.resizable && isExpanded.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "recommend-edit__resize-handle",
          onPointerdown: startResize,
          onPointermove: resize,
          onPointerup: stopResize,
          onPointercancel: stopResize,
          onLostpointercapture: finishResize
        }, null, 32))
      : _createCommentVNode("", true)
  ], 6))
}
}

})